import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface FeatureUpgradeModalContextType {
  isUpgradeModalOpen: boolean;
  modalContent: {
    featureNameKey: string; // Translation key for the feature name/title part
    customMessageKey: string; // Translation key for the specific reason
    customMessageOptions?: Record<string, any>; // Optional interpolation values for customMessageKey
  } | null;
  openUpgradeModal: (content: {
    featureNameKey: string;
    customMessageKey:string;
    customMessageOptions?: Record<string, any>;
  }) => void;
  closeUpgradeModal: () => void;
}

const FeatureUpgradeModalContext = createContext<FeatureUpgradeModalContextType | undefined>(undefined);

export const useFeatureUpgradeModal = () => {
  const context = useContext(FeatureUpgradeModalContext);
  if (!context) {
    throw new Error('useFeatureUpgradeModal must be used within a FeatureUpgradeModalProvider');
  }
  return context;
};

export const FeatureUpgradeModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<FeatureUpgradeModalContextType['modalContent']>(null);

  const openUpgradeModal = useCallback((content: FeatureUpgradeModalContextType['modalContent']) => {
    setModalContent(content);
    setIsUpgradeModalOpen(true);
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setIsUpgradeModalOpen(false);
    // It's good practice to clear content on close, though not strictly necessary
    // if openUpgradeModal always sets new content.
    // setModalContent(null);
  }, []);

  return (
    <FeatureUpgradeModalContext.Provider value={{ isUpgradeModalOpen, modalContent, openUpgradeModal, closeUpgradeModal }}>
      {children}
      {/* The actual FeatureUpgradeModal component will be rendered here globally */}
    </FeatureUpgradeModalContext.Provider>
  );
}; 